import { cn } from "@app/lib/utils";
import { isApple } from "@design-system/layouts/utils";
import { trans } from "@mongez/localization";
import { useSearchParams } from "@remix-run/react";
import { useEffect, useState } from "react";
import { SwiperSlide } from "swiper/react";
import { TabsSlider } from "./TabsSlider";

type TabsProps<T> = {
  className?: string;
  classes?: {
    container?: string;
    tabs?: string;
    tab?: string;
    tabContent?: string;
  };
  tabs: {
    label: string;
    value: string;
    data?: T;
    component: (data: T) => React.ReactNode;
  }[];
};

export function Tabs<T>({ tabs, className, classes }: TabsProps<T>) {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    const tab = searchParams.get("tab");

    if (tab) {
      setActiveTab(tab);
    } else {
      window.history.pushState(null, "", `?tab=${tabs[0].value}`);
      setActiveTab(tabs[0].value);
    }
  }, []);

  const handleSetActiveTab = (value: string) => {
    setActiveTab(value);
    window.history.pushState(null, "", `?tab=${value}`);
  };

  return (
    <div
      className={cn(
        "container flex flex-col gap-8 bg-[#f7f9fc] lg:gap-12",
        className,
        classes?.container,
      )}
    >
      <div
        className={cn(
          "flex flex-wrap items-center gap-1.5 sm:gap-3",
          classes?.tabs,
        )}
      >
        <TabsSlider>
          {tabs.map((tab) => (
            <SwiperSlide
              key={tab.value}
              className="p-1"
              style={{
                width: "fit-content",
              }}
            >
              <Tab
                key={tab.value}
                tab={tab}
                activeTab={activeTab}
                setActiveTab={handleSetActiveTab}
                className={classes?.tab}
              />
            </SwiperSlide>
          ))}
        </TabsSlider>
      </div>

      {/* Tab Content */}
      <TabContent className={classes?.tabContent}>
        {tabs
          .find((tab) => tab.value === activeTab)
          ?.component(
            tabs.find((tab) => tab.value === activeTab)?.data ?? ({} as T),
          )}
      </TabContent>
    </div>
  );
}

type TabProps<T> = {
  activeTab: string;
  setActiveTab: (value: string) => void;
  className?: string;
  tab: {
    label: string;
    value: string;
    data?: T;
    component: (data: T) => React.ReactNode;
  };
};

function Tab<T>({ tab, activeTab, setActiveTab, className }: TabProps<T>) {
  const isMac = isApple();

  return (
    <button
      type="button"
      // variant="ghost"
      title={trans(tab.label)}
      className={cn(
        "w-full shrink-0 whitespace-nowrap rounded-full border border-[#D0D0D0] bg-white px-6 py-3 text-xs font-medium text-marine focus:ring-bright-orange sm:text-sm lg:w-auto lg:text-base",
        activeTab === tab.value &&
          "bg-bright-orange text-white hover:bg-bright-orange",
        className,
        isMac ? "mac-font" : "not-italic",
      )}
      onClick={() => setActiveTab(tab.value)}
    >
      <span className="block ltr:mt-1">{trans(tab.label)}</span>
    </button>
  );
}

type TabContentProps = {
  children: React.ReactNode;
  className?: string;
};

function TabContent({ children, className }: TabContentProps) {
  return (
    <div className={cn("flex flex-col gap-6 text-marine", className)}>
      {children}
    </div>
  );
}
